import { East } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import React, { FC } from 'react';
import { Link } from '~/components/Link';
import ReportChip from '~/components/ReportChip';
import type { ColorValue } from '~/global.style';
import { cleanReportName } from '~/scripts/cleanReportName';
import { Suggestions } from '~/types/suggestions';

export interface CategoryRowProps {
  id: number;
  name: string;
  total: number;
  reports: Suggestions.Suggestion[];
  color?: ColorValue;
}

export const CategoryRow: FC<CategoryRowProps> = ({
  id,
  name,
  total,
  reports,
  color,
}) => {
  const categoryHref = `/technologies/${cleanReportName(name)}/${id}`;

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        py: 1.5,
        px: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
        gap: 2,
      }}>
      <Box sx={{ minWidth: 160, flexShrink: 0 }}>
        <Link
          href={categoryHref}
          underline="none"
          variant="body1"
          fontWeight={'bold'}
          color="textPrimary">
          {name}
        </Link>
        {total ? (
          <Typography variant="caption" color="text.secondary" display="block">
            {total.toLocaleString()} technologies
          </Typography>
        ) : null}
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: 48,
            height: '100%',
            background: 'linear-gradient(to right, transparent, white)',
            pointerEvents: 'none',
          },
        }}>
        <Stack
          direction="row"
          alignItems="center"
          flexWrap="nowrap"
          gap={1}
          sx={{
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}>
          {reports.map((report) => {
            return (
              <Link
                key={report.uuid}
                href={report.url}
                underline="none"
                hideExternalIcon
                title={report.name}>
                <ReportChip
                  item={{
                    type: 'report',
                    value: report.uuid,
                    name: report.name,
                  }}
                />
              </Link>
            );
          })}
        </Stack>
      </Box>

      <Box sx={{ flexShrink: 0 }}>
        <Link
          href={categoryHref}
          underline="none"
          variant="body2"
          fontWeight={'bold'}
          color={color || 'primary'}>
          View all
          <East sx={{ fontSize: 12, ml: 0.5, verticalAlign: 'middle' }} />
        </Link>
      </Box>
    </Stack>
  );
};
